import { Community } from "../types";

export interface FormattedAddress {
  level1Value: string;
  level2Value: string;
  level3Value: string;
  fullAddress: string;
}

/**
 * Format community address. Flat numbers like 905 stay intact (not chopped to 05).
 */
export const formatAddress = (
  community: Community,
  level1: string,
  level2: string,
  level3: string
): FormattedAddress => {
  let flat = (level3 || "").trim();

  // If user typed "C-905" / "c-905", keep the numeric flat part
  if (flat.includes("-")) {
    const parts = flat.split("-");
    flat = (parts[parts.length - 1] || flat).trim();
  }

  // Strip a leading block letter if they typed "C905"
  flat = flat.replace(/^[A-Za-z]+\s*/, "");
  flat = flat.replace(/\D/g, "");

  // Pad short values like "5" → "05"; never chop "905" down to "05"
  const minDigits = 2;
  const maxDigits = 4;
  if (flat.length > maxDigits) {
    flat = flat.slice(-maxDigits);
  }
  if (flat.length > 0 && flat.length < minDigits) {
    flat = flat.padStart(minDigits, "0");
  }

  const unitNumber = community.level3?.prefixLevel1
    ? `${level1}-${flat}`
    : flat;

  const area = community.area || community.city || "";
  const fullAddress = area
    ? `${unitNumber}, ${community.name}, ${area}`
    : `${unitNumber}, ${community.name}`;

  return {
    level1Value: level1,
    level2Value: level2,
    level3Value: flat,
    fullAddress,
  };
};

export const getLevelValues = (
  community: Community,
  levelNumber: 1 | 2 | 3
): string[] => {
  switch (levelNumber) {
    case 1:
      return community.level1.values;
    case 2:
      return community.level2.values;
    case 3:
      return community.level3.values && community.level3.values.length > 0
        ? community.level3.values
        : generateLevel3Values(community.level3.digitsCount);
    default:
      return [];
  }
};

export const generateLevel3Values = (digitsCount: number): string[] => {
  const values: string[] = [];
  const max = Math.pow(10, digitsCount) - 1;
  for (let i = 1; i <= max; i++) {
    values.push(String(i).padStart(digitsCount, "0"));
  }
  return values;
};

export const getLevelLabel = (
  community: Community,
  levelNumber: 1 | 2 | 3
): string => {
  switch (levelNumber) {
    case 1:
      return community.level1.label;
    case 2:
      return community.level2.label;
    case 3:
      return community.level3.label;
    default:
      return "";
  }
};

export const parseFormattedAddress = (
  formattedAddress: string
): { level1: string; level3: string } | null => {
  const match = formattedAddress.match(/^([A-Za-z0-9]+)-?(\d+),/);
  if (!match) return null;
  return { level1: match[1], level3: match[2] };
};
